
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ListChecks } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "@/components/ui/card";
import Timer from "@/components/pomodoro/Timer";

export default function Organizer() {
  const { t } = useTranslation();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const { data: todaysTasks = [], isLoading } = useQuery({
    queryKey: ['todays-tasks'],
    queryFn: async () => {
      const { data: tasks, error } = await supabase
        .from('tasks')
        .select(`
          id,
          content,
          due_date,
          start_date,
          column_id,
          columns (
            id,
            title,
            board_id,
            boards (
              id,
              name
            )
          )
        `)
        .gte('start_date', today.toISOString())
        .lt('start_date', new Date(today.getTime() + 24 * 60 * 60 * 1000).toISOString());

      if (error) throw error;
      return tasks || [];
    }
  });

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="flex items-center gap-3 mb-8">
        <ListChecks className="h-8 w-8" />
        <div>
          <h1 className="text-2xl font-bold">
            {t('Today\'s Tasks')}
          </h1>
          <p className="text-muted-foreground">
            {format(today, 'PPPP')}
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-8">{t('Loading...')}</div>
      ) : todaysTasks.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-center text-muted-foreground">
              {t('No tasks scheduled for today')}
            </CardTitle>
          </CardHeader>
        </Card>
      ) : (
        <div className="space-y-4">
          {todaysTasks.map((task) => (
            <Card key={task.id} className="hover:shadow-md transition-shadow">
              <CardHeader>
                <CardTitle className="text-lg">{task.content}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="text-sm text-muted-foreground">
                    <p>{t('Board')}: {task.columns?.boards?.name}</p>
                    <p>{t('List')}: {task.columns?.title}</p>
                    {task.due_date && (
                      <p>{t('Due')}: {format(new Date(task.due_date), 'PPp')}</p>
                    )}
                  </div>
                  <Timer taskId={task.id} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
